import { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';

type AdminNotification = {
  id: string;
  tipo: string;
  titulo: string;
  mensaje: string;
  leida: boolean;
  created_at: string;
};

export default function BandejaNotificacionesPage() {
  const { profile } = useAuth();
  const { sucursalActiva } = useSucursal();
  const [rows, setRows] = useState<AdminNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gymId = profile?.gimnasioId || sucursalActiva?.gimnasio_id || null;
  const unreadCount = useMemo(() => rows.filter((row) => !row.leida).length, [rows]);
  const visibleRows = onlyUnread ? rows.filter((row) => !row.leida) : rows;

  const load = async () => {
    if (!profile || (!gymId && profile.role !== 'platform_admin')) return;
    setLoading(true);
    setError(null);
    let query = supabase
      .from('notificaciones')
      .select('id,tipo,titulo,mensaje,leida,created_at')
      .eq('destino', 'admin')
      .order('created_at', { ascending: false })
      .limit(200);
    if (gymId) query = query.eq('gimnasio_id', gymId);
    const { data, error: loadError } = await query;
    if (loadError) {
      setError(loadError.message);
      setRows([]);
    } else {
      setRows((data || []) as AdminNotification[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gymId, profile?.role]);

  const markOne = async (id: string) => {
    const { error: updateError } = await supabase
      .from('notificaciones')
      .update({ leida: true })
      .eq('id', id);
    if (updateError) {
      setError(updateError.message);
      return;
    }
    setRows((current) => current.map((row) => (row.id === id ? { ...row, leida: true } : row)));
  };

  const markAll = async () => {
    if (markingAll || unreadCount === 0) return;
    setMarkingAll(true);
    setError(null);
    let query = supabase
      .from('notificaciones')
      .update({ leida: true })
      .eq('destino', 'admin')
      .eq('leida', false);
    if (gymId) query = query.eq('gimnasio_id', gymId);
    const { error: updateError } = await query;
    if (updateError) setError(updateError.message);
    else setRows((current) => current.map((row) => ({ ...row, leida: true })));
    setMarkingAll(false);
  };

  const iconFor = (type: string) => {
    if (type.includes('pago')) return 'ri-money-dollar-circle-line text-emerald-600';
    if (type.includes('reserva')) return 'ri-calendar-check-line text-blue-600';
    if (type.includes('usuario')) return 'ri-user-add-line text-teal-600';
    return 'ri-notification-3-line text-amber-600';
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Bandeja de notificaciones</h1>
            <p className="text-gray-500 mt-1">Avisos internos y actividad administrativa del gimnasio.</p>
          </div>
          <button
            type="button"
            onClick={() => void markAll()}
            disabled={unreadCount === 0 || markingAll}
            className="px-4 py-2.5 rounded-lg bg-teal-700 text-white font-medium hover:bg-teal-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <i className={`${markingAll ? 'ri-loader-4-line animate-spin' : 'ri-check-double-line'} mr-2`}></i>
            {markingAll ? 'Marcando...' : 'Marcar todas como leídas'}
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="font-semibold text-gray-900">{rows.length} mensajes</span>
              {unreadCount > 0 && <span className="px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold">{unreadCount} sin leer</span>}
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={onlyUnread} onChange={(event) => setOnlyUnread(event.target.checked)} />
              Solo sin leer
            </label>
          </div>

          {error && <div className="p-4 bg-red-50 text-red-700 border-b border-red-100">{error}</div>}
          {loading ? (
            <div className="p-12 text-center text-gray-500"><i className="ri-loader-4-line animate-spin text-2xl"></i><p className="mt-2">Cargando mensajes...</p></div>
          ) : visibleRows.length === 0 ? (
            <div className="p-12 text-center text-gray-500"><i className="ri-notification-off-line text-4xl text-gray-300"></i><p className="mt-3">No hay mensajes para mostrar.</p></div>
          ) : (
            <div className="divide-y divide-gray-100">
              {visibleRows.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => !row.leida && void markOne(row.id)}
                  className={`w-full p-5 flex items-start gap-4 text-left hover:bg-gray-50 ${row.leida ? 'bg-white' : 'bg-teal-50/60'}`}
                >
                  <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center shrink-0"><i className={`${iconFor(row.tipo)} text-xl`}></i></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-4">
                      <p className={`text-gray-900 ${row.leida ? 'font-medium' : 'font-bold'}`}>{row.titulo}</p>
                      {!row.leida && <span className="w-2.5 h-2.5 bg-teal-500 rounded-full shrink-0"></span>}
                    </div>
                    <p className="text-gray-600 mt-1">{row.mensaje}</p>
                    <p className="text-xs text-gray-400 mt-2">{new Date(row.created_at).toLocaleString('es-CL')}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
